import { doodBlockConfig, propertyValue } from "./dood-block-config";
import {
  buildOrderedBlockDescriptor,
  type DescriptorFieldOrderItem,
} from "./descriptor-field-order";
import type { IBlockDescriptor } from "./descriptor";

/** Serializable product row used in CB2 cart suggestion preview scenarios. */
export type CartSuggestionCardsPreviewProduct = {
  id: string;
  _id: string;
  shop_id: string;
  name: string;
  description?: string;
  price: number;
  final_price: number;
  vat: string;
  available: boolean;
  additions_group: string;
  categories: readonly unknown[];
  additions: { products: readonly unknown[] };
  images?: {
    thumbnail?: { url: string; width?: number; height?: number };
  };
};

/**
 * Serializable preview VM for `CartSuggestionCardsBlockComponent`.
 * Kept in content-builder so dashboard + webapp share the same transport shape.
 */
export type CartSuggestionCardsBlockScenarioContent = {
  /** Loaded suggestions; an empty array means the block is hidden. */
  suggestions: readonly CartSuggestionCardsPreviewProduct[];
};
/** Legacy CB1 field order for `CartSuggestionCardsBlock`. */
export const CART_SUGGESTION_CARDS_LEGACY_FIELD_ORDER = [
  "heading",
  "leading",
  "suggestions_set",
] as const satisfies readonly DescriptorFieldOrderItem[];

const {
  cartSuggestionCardsBlockDescriptor: _generatedCartSuggestionCardsBlockDescriptor,
  cartSuggestionCardsBlockBlocDefinition,
  cartSuggestionCardsBlockDashboardLayout,
  cartSuggestionCardsBlockConfig,
  cartSuggestionCardsBlockResolveConfig,
  cartSuggestionCardsBlockProperties,
  cartSuggestionCardsBlockDefinitionSetupScenarios,
} = doodBlockConfig(
  {
    name: "Cart - Suggestion cards",
    componentType: "CartSuggestionCardsBlockComponent",
    category: "cart",
    description: "Product suggestion cards displayed in the cart.",
    keywords: ["cart", "suggestions", "products"],
    allowedIn: [
      { bloc: 'CartPageComponent', slot: '*' },
    ],


    sharedFields: {},
  },
  () => ({
    heading: propertyValue({
      type: "text",
      value: "",
      options: { label: "pages.blocks.cart-suggestion-cards.heading.label" },
    }),
    leading: propertyValue({
      type: "text",
      value: "",
      options: { label: "pages.blocks.cart-suggestion-cards.leading.label" },
    }),
    suggestions_set: propertyValue({
      type: "suggestions_set_picker",
      value: "",
      options: {
        label: "pages.blocks.cart-suggestion-cards.suggestions-set.label",
      },
    }),
  }),
  () => ({
    tabs: [
      {
        label: "pages.blocks.cart-suggestion-cards.tab.content.label",
        fieldsets: [
          {
            legend: "pages.blocks.cart-suggestion-cards.fieldset.texts.label",
            fields: ["heading", "leading"],
          },
          {
            legend:
              "pages.blocks.cart-suggestion-cards.fieldset.suggestions.label",
            fields: ["suggestions_set"],
          },
        ],
      },
    ],
  })
);

export function cartSuggestionCardsBlockDescriptor(): IBlockDescriptor;
export function cartSuggestionCardsBlockDescriptor(
  fieldOrder: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor;
export function cartSuggestionCardsBlockDescriptor(
  fieldOrder?: readonly DescriptorFieldOrderItem[],
): IBlockDescriptor {
  if (fieldOrder === undefined) {
    return _generatedCartSuggestionCardsBlockDescriptor;
  }
  return buildOrderedBlockDescriptor(
    _generatedCartSuggestionCardsBlockDescriptor,
    cartSuggestionCardsBlockProperties,
    fieldOrder,
  );
}

export {
  cartSuggestionCardsBlockBlocDefinition,
  cartSuggestionCardsBlockDashboardLayout,
  cartSuggestionCardsBlockConfig,
  cartSuggestionCardsBlockResolveConfig,
  cartSuggestionCardsBlockProperties,
  cartSuggestionCardsBlockDefinitionSetupScenarios,
};

export type CartSuggestionCardsBlockConfig = typeof cartSuggestionCardsBlockConfig;
